import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import CheckBox from '../../../../../components/common/CheckBox/CheckBox';
import { Button } from '../../../../../components/ui/Button/Button';
import { AlertsConfiguration, AlertsConfigurationRequest } from '../../../../../API/types/Devices';
import useGetGlobalAlertSettingsByFleetId from '../../../../../API/hooks/devices/useGetAlertsGlobalConfiguration';
import useGetDeviceAlertSettingsByDeviceId from '../../../../../API/hooks/devices/useGetAlertsConfiguration';
import usePostGlobalAlertSettingsByFleetId from '../../../../../API/hooks/devices/usePostAlertGlobalConfiguration';
import usePostDeviceAlertSettingsByDeviceId from '../../../../../API/hooks/devices/usePostAlertsConfiguration';
import S from './AlertsConfigItemStyles';
import Input from '../../../../../components/common/Input/Input';
import Modal from '../../../../../components/common/Modal/Modal';
import InfoModal from '../../../../../components/common/InfoModal/InfoModal';
import Select from '../../../../../components/common/Select/Select';
import { DEFAULT_ALERT_LEVELS } from '../../../../../constants/AlertConfig';

type Props = {
  name: string,
  global?: boolean,
  fleetId: string,
  deviceId: string,
  label?: string,
  isOnline?: boolean;
  toggleModal: (closed: boolean) => void;
  fields?: any[],
};

function AlertsConfigItem({
  global, name, fleetId, deviceId, label, isOnline, toggleModal, fields,
}: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const [confirmSaveModalOpen, toggleConfirmSaveModal] = useState(false);
  const [deviceOfflineModalOpen, toggleDeviceOfflineSaveModal] = useState(false);
  // const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitSuccess = (): void => {
    if (!global && !isOnline) {
      toggleDeviceOfflineSaveModal(true);
    } else {
      toggleConfirmSaveModal(true);
    }
  };
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  const { data: fleetConfig, isLoading: isLoadingFleet } = useGetGlobalAlertSettingsByFleetId(fleetId, name);
  const fleetConfigIsExisting = fleetConfig;
  const fleetConfigIsActive = fleetConfig?.isActive;
  // eslint-disable-next-line max-len, react-hooks/rules-of-hooks
  const { data: config, isLoading, error: failure } = global ? useGetGlobalAlertSettingsByFleetId(fleetId, name) : useGetDeviceAlertSettingsByDeviceId(deviceId || '', fleetId, name);
  // eslint-disable-next-line max-len, react-hooks/rules-of-hooks
  const { mutate } = global ? usePostGlobalAlertSettingsByFleetId(onSubmitSuccess, onSubmitFail) : usePostDeviceAlertSettingsByDeviceId(onSubmitSuccess, onSubmitFail);
  const [riskLevels, setRiskLevels] = useState<{}>(global ? DEFAULT_ALERT_LEVELS[name] : {});
  const noConfig = failure && ((failure as any).statusCode === 404);
  const getConfigError = failure && ((failure as any).statusCode !== 404);

  const fieldsKeys = fields?.map((f: any) => f.name);

  const fieldsValues: any = {};
  fields?.forEach((v: any) => {
    fieldsValues[v.name] = v.defaultValue;
  });

  const defaultConfig: any = {
    isActive: true,
    ...fieldsValues,
  };

  const formik = useFormik({
    initialValues: defaultConfig,
    onSubmit: (values) => {
      if (global) {
        const payload = {
          ...values,
          level: Object.keys(riskLevels).length ? riskLevels : null,
        };
        mutate({ payload, fleetId, alertName: name } as unknown as AlertsConfigurationRequest);
      } else {
        delete values.isActive;
        mutate({
          payload: values, deviceId, fleetId, alertName: name,
        } as unknown as AlertsConfigurationRequest);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  useEffect(() => {
    const fieldsUpdatedValues: any = {};
    fieldsKeys?.forEach((key: string) => {
      fieldsUpdatedValues[key] = (config && config[key as keyof AlertsConfiguration] !== undefined) ? config[key as keyof AlertsConfiguration] : defaultConfig[key];
    });
    formik.setValues({
      isActive: config ? config.isActive : defaultConfig.isActive,
      ...fieldsUpdatedValues,
    }, false);
    if (global && config?.level && Object.keys(config?.level).length) {
      setRiskLevels({
        ...riskLevels,
        ...config.level,
      });
    }
  }, [config]);

  const handleCheckboxChange = (id: string, state: boolean): void => {
    formik.setFieldValue(id, state);
    formik.setSubmitting(false);
    setSubmitError('');
  };

  const handleInputChange = (e: any): void => {
    formik.setFieldValue(e.target.name, e.target.value);
    formik.setSubmitting(false);
    setSubmitError('');
  };

  const globalSettingIsActivated = fleetConfigIsExisting && fleetConfigIsActive && !global;

  if (isLoading || isLoadingFleet || getConfigError) {
    return (
      <S.Wrapper>
        <S.RowContainer style={{ display: 'block' }}>
          <p className={getConfigError ? 'loading error' : 'loading'}>
            {getConfigError ? 'Error of data fetching!' : 'Loading...'}
          </p>
        </S.RowContainer>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <p>{label}: {isLoading || isLoadingFleet ? '  ... Loading ...' : ''}</p>
      <form onSubmit={formik.handleSubmit}>
        <div className="alerts-details-container">
          <S.RowContainer className={global ? '' : 'hidden'}>
            <CheckBox
              label="Apply to all devices in fleet"
              state={formik.values.isActive}
              onChange={handleCheckboxChange}
              name="isActive"
              disabled={isLoading}
            />
          </S.RowContainer>
          {!!fields?.length && (
            <S.RowContainer className={globalSettingIsActivated ? 'hidden' : ''}>
              {fields.map((field: any, i: number) => (field.text ? (
                <div className="input-element">
                  <Input
                    label={field.label}
                    type={field.type}
                    key={field.name}
                    name={field.name}
                    placeholder={field.placeholder}
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={field.defaultValue}
                    value={formik.values[field.name]}
                    disabled={(global && !formik.values.isActive) || isLoading || isLoadingFleet}
                  />
                </div>
              ) : (
                <div className="checkbox-element">
                  <CheckBox
                    label={field.label}
                    key={field.name}
                    state={formik.values[field.name]}
                    onChange={handleCheckboxChange}
                    name={field.name}
                    disabled={(global && !formik.values.isActive) || isLoading || isLoadingFleet}
                  />
                </div>
              )))}
            </S.RowContainer>
          )}
          {!!Object.keys(riskLevels || {}).length && (
            <S.RowContainer className="alerts-risk-level-container">
              <h1>Risk levels:</h1>
              {Object.entries(riskLevels).map(([level, risk]) => (
                <div key={level} className="alerts-risk-level-element">
                  <p>{`level ${level}:`}</p>
                  <Select
                    value={risk as string}
                    name={level}
                    simple
                    options={[
                      {
                        label: 'Low',
                        value: 'low',
                      },
                      {
                        label: 'Medium',
                        value: 'medium',
                      },
                      {
                        label: 'Urgent',
                        value: 'urgent',
                      },
                    ]}
                    onChange={(e: any) => {
                      const newRiskLevels = {
                        ...riskLevels,
                        [level]: e.target.value,
                      };
                      setRiskLevels(newRiskLevels);
                    }}
                  />
                </div>
              ))}
            </S.RowContainer>
          )}
          <S.RowContainer className={globalSettingIsActivated ? '' : 'hidden'}>
            <p className="message warning">Global fleet setting was applied. No possibility to configure alarms for individual device.</p>
          </S.RowContainer>
          <S.RowContainer className={!global && !globalSettingIsActivated && noConfig ? '' : 'hidden'}>
            <p className="message warning">Note: Device is using the default configuration. Please set/change new parameters if needed.</p>
          </S.RowContainer>
          <S.RowContainer className={submitError ? '' : 'hidden'}>
            <p className="message error">Submitting error: {typeof submitError === 'string' ? submitError : submitError?.map((error) => error)}</p>
          </S.RowContainer>
        </div>
        <div className="actions-container">
          <Button
            height="50px"
            onClick={() => toggleModal(false)}
            type="button"
          >
            Close
          </Button>
          <Button
            className="save-button"
            height="50px"
            type="submit"
            disabled={
              formik.isSubmitting || !!submitError || (!global && (globalSettingIsActivated as boolean))
            }
          >
            Save
          </Button>
        </div>
      </form>
      {confirmSaveModalOpen && (
        <Modal
          title="Device settings"
          state={confirmSaveModalOpen}
          toggleState={toggleConfirmSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="New settings were submitted!"
            onClose={() => toggleConfirmSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {deviceOfflineModalOpen && (
        <Modal
          title="Device settings"
          state={deviceOfflineModalOpen}
          toggleState={toggleDeviceOfflineSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="Device is offline. Changes will be applied after device become online!"
            onClose={() => toggleDeviceOfflineSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default AlertsConfigItem;
